import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { MemoryRouter } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast from 'react-hot-toast';
import {
  Plus, ListChecks, Pencil, Trash2, Star, Eye, Send, ArrowLeft,
  ChevronLeft, ChevronRight, ImageIcon, Loader2, Clock,
} from 'lucide-react';
import { blogsApi } from '../../lib/api/blogsApi';
import AnimatedModal from '../../components/ui/AnimatedModal';
// Reuse the EXACT public styles so the admin screens never drift from the live site.
import { blogStyles, postStyles } from '../public/blogStyles';
import BlogPostSidebar from '../public/BlogPostSidebar';

// Manage-screen overlays layered on top of the real public card markup.
const MANAGE_OVERRIDES = `
  .tc-blog-manage .tc-blog-featured, .tc-blog-manage .tc-blog-card { cursor: default; }
  .tc-blog-manage .tc-blog-card:hover, .tc-blog-manage .tc-blog-featured:hover { transform: none; }
  .tc-blog-manage .tc-card-shell { position: relative; }
  .tc-card-actions { position: absolute; top: 10px; right: 10px; display: flex; gap: 6px; z-index: 6; }
  .tc-card-actions button { width: 30px; height: 30px; border-radius: 8px; border: none; cursor: pointer;
    background: rgba(255,255,255,0.96); box-shadow: 0 1px 5px rgba(0,0,0,0.16);
    display: flex; align-items: center; justify-content: center; color: #475569; transition: color .15s; }
  .tc-card-actions button:hover { color: #0f172a; }
  .tc-card-actions button.danger:hover { color: #dc2626; }
  .tc-card-actions button.feature:hover { color: #f59e0b; }
  .tc-card-move { position: absolute; top: 10px; left: 10px; z-index: 6; display: flex; gap: 6px; }
  .tc-card-move button { width: 30px; height: 30px; border-radius: 8px; border: none; cursor: pointer;
    background: rgba(255,255,255,0.96); box-shadow: 0 1px 5px rgba(0,0,0,0.16);
    display: flex; align-items: center; justify-content: center; color: #475569; }
  .tc-card-move button:hover:not(:disabled) { color: #0f172a; }
  .tc-card-move button:disabled { opacity: 0.4; cursor: default; }
  .tc-card-status { position: absolute; top: 12px; left: 50%; transform: translateX(-50%); z-index: 6;
    font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 9999px; }
  .tc-card-status.draft { background: #e2e8f0; color: #475569; }
  .tc-card-status.published { background: #dcfce7; color: #15803d; }
  /* Compact the marketing hero so it fits the admin panel. */
  .tc-blog-manage .tc-blog-hero { padding: 8px 24px 28px; }
  .tc-blog-manage .tc-blog-hero-title { font-size: 34px; }
`;

const EMPTY_FORM = {
  title: '', author: 'TalentCrux Team', excerpt: '', bodyHtml: '',
  coverImageUrl: '', readTime: '', metaTitle: '', metaDescription: '',
  publishedAt: '', pinned: false, status: 'draft',
};

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

const postToForm = (p) => ({
  title: p.title || '', author: p.author || 'TalentCrux Team', excerpt: p.excerpt || '',
  bodyHtml: p.bodyHtml || '', coverImageUrl: p.coverImageUrl || '', readTime: p.readTime || '',
  metaTitle: p.metaTitle || '', metaDescription: p.metaDescription || '',
  publishedAt: toDateInput(p.publishedAt), pinned: !!p.pinned, status: p.status || 'draft',
});

export default function InternalBlog() {
  const qc = useQueryClient();
  const [view, setView] = useState('landing'); // 'landing' | 'manage' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const quillRef = useRef(null);

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['blogs', 'admin'],
    queryFn: blogsApi.listAdmin,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['blogs'] });

  const saveMutation = useMutation({
    mutationFn: ({ id, body }) => (id ? blogsApi.update(id, body) : blogsApi.create(body)),
    onSuccess: (saved) => {
      invalidate();
      toast.success(saved.status === 'published' ? 'Published — live on /blog' : 'Draft saved');
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => blogsApi.remove(id),
    onSuccess: () => { invalidate(); toast.success('Post deleted'); },
    onError: () => toast.error('Delete failed'),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids) => blogsApi.reorder(ids),
    onSuccess: invalidate,
    onError: () => toast.error('Reorder failed'),
  });

  /* ── Editor: react-quill inline image upload ── */
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;
      try {
        const url = await blogsApi.uploadImage(file);
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, 'image', url);
        editor.setSelection(range.index + 1);
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Image upload failed');
      }
    };
    input.click();
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'link', 'image'],
          ['clean'],
        ],
        handlers: { image: imageHandler },
      },
    }),
    [imageHandler]
  );

  /* ── Actions ── */
  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setView('edit'); };
  const openEdit = (p) => { setEditingId(p._id); setForm(postToForm(p)); setView('edit'); };

  const set = (key) => (e) => {
    const val = e && e.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: val }));
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await blogsApi.uploadImage(file);
      setForm((f) => ({ ...f, coverImageUrl: url }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Cover upload failed');
    } finally {
      setCoverUploading(false);
    }
  };

  const buildPayload = (overrides = {}) => ({
    ...form,
    publishedAt: form.publishedAt || undefined,
    ...overrides,
  });

  const saveDraft = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const saved = await saveMutation.mutateAsync({ id: editingId, body: buildPayload({ status: 'draft' }) });
    setEditingId(saved._id);
    setForm((f) => ({ ...f, status: 'draft' }));
  };

  const confirmPublish = async () => {
    const saved = await saveMutation.mutateAsync({ id: editingId, body: buildPayload({ status: 'published' }) });
    setConfirmOpen(false);
    setEditingId(saved._id);
    setView('manage');
  };

  const makeFeatured = (p) => saveMutation.mutate({ id: p._id, body: { ...postToForm(p), pinned: true } });

  // Move a non-featured post one slot earlier (-1) or later (+1) and persist the order.
  const move = (post, dir) => {
    const list = posts.filter((p) => !p.pinned);
    const index = list.findIndex((p) => p._id === post._id);
    const target = index + dir;
    if (index < 0 || target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    reorderMutation.mutate(list.map((p) => p._id));
  };

  const featured = posts.find((p) => p.pinned);
  const rest = posts.filter((p) => !p.pinned);

  /* ───────────────────────────── Landing ───────────────────────────── */
  if (view === 'landing') {
    return (
      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Blogs</h1>
        <p className="text-gray-500 mb-8">Create and manage posts on the public blog.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <button onClick={openCreate} className="text-left rounded-xl border border-gray-200 bg-white p-6 hover:border-blue-400 hover:shadow-md transition">
            <Plus className="text-blue-600 mb-3" size={26} />
            <div className="text-lg font-semibold text-gray-900">Create Blog</div>
            <div className="text-sm text-gray-500 mt-1">Write a new post, preview it, then publish.</div>
          </button>
          <button onClick={() => setView('manage')} className="text-left rounded-xl border border-gray-200 bg-white p-6 hover:border-blue-400 hover:shadow-md transition">
            <ListChecks className="text-blue-600 mb-3" size={26} />
            <div className="text-lg font-semibold text-gray-900">Manage Blogs</div>
            <div className="text-sm text-gray-500 mt-1">Edit, delete, feature and reorder existing posts.</div>
          </button>
        </div>
      </div>
    );
  }

  /* ───────────────────────────── Manage (real /blog grid) ───────────── */
  if (view === 'manage') {
    return (
      <div>
        <style>{blogStyles}</style>
        <style>{MANAGE_OVERRIDES}</style>

        <div className="flex items-center justify-between mb-2">
          <button onClick={() => setView('landing')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft size={15} /> Back
          </button>
          <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            <Plus size={16} /> New Post
          </button>
        </div>

        {isLoading ? (
          <div className="text-gray-400 flex items-center gap-2 p-8"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : posts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-gray-500">No posts yet.</div>
        ) : (
          <div className="tc-blog tc-blog-manage" style={{ background: 'transparent' }}>
            <section className="tc-blog-hero">
              <div className="tc-blog-hero-center">
                <p className="tc-blog-hero-label">Blog</p>
                <h1 className="tc-blog-hero-title">From the <span>TalentCrux</span> desk</h1>
              </div>
            </section>

            {featured && (
              <div className="tc-blog-featured-wrap">
                <div className="tc-card-shell">
                  <FeaturedCard post={featured} />
                  <CardControls post={featured} featured onEdit={openEdit} onDelete={deleteMutation.mutate} onFeature={makeFeatured} />
                  <span className="tc-card-status published">★ Featured · {featured.status}</span>
                </div>
              </div>
            )}

            {rest.length > 0 && (
              <>
                <div className="tc-blog-divider">
                  <span className="tc-blog-divider-label">Latest — use ◀ ▶ to reorder</span>
                  <hr className="tc-blog-divider-line" />
                </div>
                <section className="tc-blog-section">
                  <div className="tc-blog-section-inner">
                    <div className="tc-blog-grid">
                      {rest.map((p, i) => (
                        <div key={p._id} className="tc-card-shell">
                          <GridCard post={p} />
                          <span className="tc-card-move">
                            <button title="Move earlier" disabled={i === 0} onClick={() => move(p, -1)}>
                              <ChevronLeft size={15} />
                            </button>
                            <button title="Move later" disabled={i === rest.length - 1} onClick={() => move(p, 1)}>
                              <ChevronRight size={15} />
                            </button>
                          </span>
                          <span className={`tc-card-status ${p.status}`}>{p.status}</span>
                          <CardControls post={p} onEdit={openEdit} onDelete={deleteMutation.mutate} onFeature={makeFeatured} />
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  /* ───────────────────────────── Editor (form + matching preview) ───── */
  const previewPost = { ...form, publishedAt: form.publishedAt || new Date().toISOString(), readTime: form.readTime || 'Auto' };

  return (
    <div className="max-w-3xl tc-blog-editor">
      <style>{blogEditorStyles}</style>
      <button onClick={() => setView('manage')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft size={15} /> Back to Manage
      </button>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{editingId ? 'Edit Blog' : 'Create Blog'}</h1>

      <div className="bf-sections">
        <Section title="Basics" subtitle="The essentials shown on blog cards and at the top of the post.">
          <Field label="Title" required>
            <input className="bf-input" value={form.title} onChange={set('title')} placeholder="Post title" />
          </Field>

          <div className="bf-grid-2">
            <Field label="Author / Team">
              <input className="bf-input" value={form.author} onChange={set('author')} />
            </Field>
            <Field label="Publish date">
              <input type="date" className="bf-input" value={form.publishedAt} onChange={set('publishedAt')} />
            </Field>
          </div>

          <Field label="Excerpt" hint="Short summary shown on blog cards.">
            <textarea className="bf-textarea" rows={2} value={form.excerpt} onChange={set('excerpt')} placeholder="Short summary shown on cards" />
          </Field>

          <Field label="Read time" hint="Leave blank to auto-compute from word count.">
            <input className="bf-input" value={form.readTime} onChange={set('readTime')} placeholder="Auto (e.g. 6 min read)" />
          </Field>
        </Section>

        <Section title="Cover image" subtitle="Used as the post hero and the card thumbnail.">
          <div className="bf-cover">
            {form.coverImageUrl ? (
              <img src={form.coverImageUrl} alt="cover" className="bf-cover-thumb" />
            ) : (
              <div className="bf-cover-empty"><ImageIcon size={22} /></div>
            )}
            <div>
              <label className="bf-upload">
                {coverUploading ? <Loader2 className="animate-spin" size={15} /> : <ImageIcon size={15} />}
                {form.coverImageUrl ? 'Replace image' : 'Upload image'}
                <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} disabled={coverUploading} />
              </label>
              <div className="bf-cover-hint">JPEG, PNG, WebP or GIF · up to 50MB.</div>
            </div>
          </div>
        </Section>

        <Section title="Content" subtitle="The full body of the post.">
          <div className="bf-editor">
            <ReactQuill ref={quillRef} theme="snow" value={form.bodyHtml} onChange={set('bodyHtml')} modules={quillModules} />
          </div>
        </Section>

        <Section title="SEO" subtitle="Optional — controls how the post appears in search results.">
          <Field label="Meta title" hint="Defaults to the post title.">
            <input className="bf-input" value={form.metaTitle} onChange={set('metaTitle')} placeholder={form.title || 'Post title'} />
          </Field>
          <Field label="Meta description" hint="Defaults to the excerpt.">
            <textarea className="bf-textarea" rows={2} value={form.metaDescription} onChange={set('metaDescription')} placeholder={form.excerpt || 'Short summary'} />
          </Field>
        </Section>

        <div className="bf-actions">
          <button onClick={saveDraft} disabled={saveMutation.isPending} className="bf-btn bf-btn-outline">
            Save Draft
          </button>
          <button onClick={() => setPreviewOpen(true)} className="bf-btn bf-btn-primary">
            <Eye size={16} /> Preview &amp; Publish
          </button>
          <div className="bf-actions-status">{editingId ? `Editing · ${form.status}` : 'New post · draft'}</div>
        </div>
      </div>

      {/* Pixel-identical preview — the real BlogPost markup + styles */}
      <AnimatedModal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} contentClassName="max-w-6xl rounded-2xl bg-white overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <span className="text-sm font-semibold text-gray-700">Preview — exactly how it appears on /blog</span>
          <button onClick={() => setPreviewOpen(false)} className="text-sm text-gray-500 hover:text-gray-800">Close</button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto bg-[#f8f9fb]">
          <IframePreview post={previewPost} />
        </div>
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-3">
          <button onClick={() => setPreviewOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Keep editing</button>
          <button onClick={() => { setPreviewOpen(false); setConfirmOpen(true); }} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            <Send size={15} /> Looks good — Publish
          </button>
        </div>
      </AnimatedModal>

      {/* Publish confirm */}
      <AnimatedModal isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} contentClassName="max-w-md rounded-2xl bg-white p-6">
        <h3 className="text-lg font-bold text-gray-900">Publish this post live?</h3>
        <p className="mt-2 text-sm text-gray-500">
          “{form.title || 'Untitled'}” will become publicly visible on <span className="font-medium">/blog</span> immediately.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={() => setConfirmOpen(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={confirmPublish} disabled={saveMutation.isPending} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            {saveMutation.isPending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Publish now
          </button>
        </div>
      </AnimatedModal>
    </div>
  );
}

// Editor styling — mirrors the public Book-a-Demo form (DemoRequest.js): soft
// slate input fills, a blue focus ring, and a gradient pill CTA. Scoped under
// `.tc-blog-editor` so none of it leaks into the rest of the /internal portal.
const blogEditorStyles = `
  .tc-blog-editor { --bf-blue: #007BFF; }
  .tc-blog-editor .bf-sections { display: flex; flex-direction: column; gap: 18px; }

  .tc-blog-editor .bf-section {
    background: #fff; border: 1px solid #e8edf3; border-radius: 16px;
    padding: 22px 22px 24px; box-shadow: 0 1px 2px rgba(15,23,42,0.04);
  }
  .tc-blog-editor .bf-section-head { margin-bottom: 18px; }
  .tc-blog-editor .bf-section-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; }
  .tc-blog-editor .bf-section-sub { font-size: 12.5px; color: #94a3b8; margin-top: 3px; }
  .tc-blog-editor .bf-stack { display: flex; flex-direction: column; gap: 16px; }

  .tc-blog-editor .bf-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  @media (max-width: 640px) { .tc-blog-editor .bf-grid-2 { grid-template-columns: 1fr; } }

  .tc-blog-editor .bf-field { display: flex; flex-direction: column; }
  .tc-blog-editor .bf-label { font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 7px; }
  .tc-blog-editor .bf-required { color: #dc2626; margin-left: 2px; }
  .tc-blog-editor .bf-hint { font-size: 12px; color: #94a3b8; margin-top: 6px; }

  .tc-blog-editor .bf-input, .tc-blog-editor .bf-textarea {
    width: 100%; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px;
    padding: 11px 13px; font-size: 14px; color: #0f172a; font-family: inherit;
    transition: border-color .15s, box-shadow .15s, background .15s; outline: none;
  }
  .tc-blog-editor .bf-input::placeholder, .tc-blog-editor .bf-textarea::placeholder { color: #94a3b8; }
  .tc-blog-editor .bf-input:hover, .tc-blog-editor .bf-textarea:hover { border-color: #cbd5e1; }
  .tc-blog-editor .bf-input:focus, .tc-blog-editor .bf-textarea:focus {
    border-color: var(--bf-blue); background: #fff; box-shadow: 0 0 0 4px rgba(0,123,255,0.10);
  }
  .tc-blog-editor .bf-textarea { resize: vertical; min-height: 62px; line-height: 1.5; }

  /* Cover image */
  .tc-blog-editor .bf-cover { display: flex; align-items: center; gap: 16px; }
  .tc-blog-editor .bf-cover-thumb { height: 84px; width: 150px; border-radius: 12px; object-fit: cover; border: 1.5px solid #e2e8f0; }
  .tc-blog-editor .bf-cover-empty {
    height: 84px; width: 150px; border-radius: 12px; background: #f8fafc; border: 1.5px dashed #cbd5e1;
    display: flex; align-items: center; justify-content: center; color: #94a3b8;
  }
  .tc-blog-editor .bf-upload {
    display: inline-flex; align-items: center; gap: 8px; border: 1.5px solid #e2e8f0; background: #fff;
    border-radius: 10px; padding: 10px 14px; font-size: 13px; font-weight: 600; color: #334155;
    cursor: pointer; transition: border-color .15s, background .15s;
  }
  .tc-blog-editor .bf-upload:hover { border-color: #cbd5e1; background: #f8fafc; }
  .tc-blog-editor .bf-cover-hint { font-size: 12px; color: #94a3b8; margin-top: 8px; }

  /* Quill editor — round + match the input focus ring */
  .tc-blog-editor .bf-editor {
    background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden;
    transition: border-color .15s, box-shadow .15s;
  }
  .tc-blog-editor .bf-editor:focus-within { border-color: var(--bf-blue); box-shadow: 0 0 0 4px rgba(0,123,255,0.10); }
  .tc-blog-editor .bf-editor .ql-toolbar.ql-snow { border: none; border-bottom: 1px solid #eef2f7; background: #fbfcfe; }
  .tc-blog-editor .bf-editor .ql-container.ql-snow { border: none; font-size: 15px; font-family: inherit; }
  .tc-blog-editor .bf-editor .ql-editor { min-height: 260px; }

  /* Action bar */
  .tc-blog-editor .bf-actions { display: flex; align-items: center; gap: 12px; padding-top: 4px; }
  .tc-blog-editor .bf-actions-status { margin-left: auto; font-size: 12px; color: #94a3b8; }
  .tc-blog-editor .bf-btn {
    display: inline-flex; align-items: center; gap: 8px; border-radius: 9999px; padding: 11px 20px;
    font-size: 13.5px; font-weight: 700; cursor: pointer; border: 1.5px solid transparent;
    transition: transform .15s, box-shadow .15s, background .15s, border-color .15s;
  }
  .tc-blog-editor .bf-btn:disabled { opacity: 0.55; cursor: default; }
  .tc-blog-editor .bf-btn-outline { background: #fff; border-color: #e2e8f0; color: #334155; }
  .tc-blog-editor .bf-btn-outline:hover:not(:disabled) { border-color: #cbd5e1; background: #f8fafc; }
  .tc-blog-editor .bf-btn-primary {
    background: linear-gradient(135deg, #007BFF, #0056b3); color: #fff; box-shadow: 0 6px 16px rgba(0,123,255,0.25);
  }
  .tc-blog-editor .bf-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(0,123,255,0.32); }
`;

function Section({ title, subtitle, children }) {
  return (
    <section className="bf-section">
      <div className="bf-section-head">
        <div className="bf-section-title">{title}</div>
        {subtitle && <div className="bf-section-sub">{subtitle}</div>}
      </div>
      <div className="bf-stack">{children}</div>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="bf-field">
      <span className="bf-label">
        {label}
        {required && <span className="bf-required" aria-hidden="true">*</span>}
      </span>
      {children}
      {hint && <span className="bf-hint">{hint}</span>}
    </label>
  );
}

/* Manage-card action overlay (edit / delete / feature). */
function CardControls({ post, featured, onEdit, onDelete, onFeature }) {
  return (
    <div className="tc-card-actions">
      {!featured && (
        <button className="feature" title="Make featured" onClick={() => onFeature(post)}><Star size={15} /></button>
      )}
      <button title="Edit" onClick={() => onEdit(post)}><Pencil size={15} /></button>
      <button
        className="danger"
        title="Delete"
        onClick={() => { if (window.confirm(`Delete “${post.title}”? This cannot be undone.`)) onDelete(post._id); }}
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

/* Featured card — identical markup to the public FeaturedPost (minus the Link). */
function FeaturedCard({ post }) {
  return (
    <div className="tc-blog-featured">
      <div className="tc-blog-featured-img-wrap">
        {post.coverImageUrl
          ? <img className="tc-blog-featured-img" src={post.coverImageUrl} alt={post.title} />
          : <div className="tc-blog-featured-img" style={{ background: post.coverGradient || '#e2e8f0' }} />}
      </div>
      <div className="tc-blog-featured-body">
        {post.category && <p className="tc-blog-featured-category">{post.category}</p>}
        <h2 className="tc-blog-featured-title">{post.title}</h2>
        <p className="tc-blog-featured-excerpt">{post.excerpt}</p>
        <div className="tc-blog-featured-footer">
          <span>{formatDate(post.publishedAt)}</span>
          <span><Clock size={12} /> {post.readTime}</span>
        </div>
      </div>
    </div>
  );
}

/* Grid card — identical markup to the public BlogCard (minus the Link). */
function GridCard({ post }) {
  return (
    <div className="tc-blog-card">
      <div className="tc-blog-card-img-wrap">
        {post.coverImageUrl
          ? <img className="tc-blog-card-img" src={post.coverImageUrl} alt={post.title} />
          : <div className="tc-blog-card-img" style={{ background: post.coverGradient || '#e2e8f0' }} />}
      </div>
      <div className="tc-blog-card-body">
        {post.category && <p className="tc-blog-card-category">{post.category}</p>}
        <h3 className="tc-blog-card-title">{post.title}</h3>
        <p className="tc-blog-card-excerpt">{post.excerpt}</p>
        <div className="tc-blog-card-footer">
          <div className="tc-blog-card-footer-left">
            <span>{formatDate(post.publishedAt)}</span>
            <span><Clock size={11} /> {post.readTime}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Fixed desktop viewport for the preview iframe — so the page's media queries
// evaluate exactly as they do on a real desktop /blog visit, then we scale the
// whole frame down to fit whatever width the modal gives us.
const PREVIEW_VIEWPORT = 1280;

function buildPreviewDoc(post) {
  // MemoryRouter supplies the router context BlogPostSidebar's useNavigate needs.
  const body = renderToStaticMarkup(
    <MemoryRouter>
      <PostPreview post={post} />
    </MemoryRouter>
  );
  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=${PREVIEW_VIEWPORT}">
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Funnel+Display:wght@700&display=swap" rel="stylesheet">
<style>html,body{margin:0;padding:0;background:#fff;}.tc-blogpost{padding:0 24px;}</style>
<style>${postStyles}</style>
</head><body>${body}</body></html>`;
}

/* True-to-life preview: the real page rendered at a desktop viewport inside an
 * iframe, scaled down to fit the modal so proportions match /blog exactly. */
function IframePreview({ post }) {
  const wrapRef = useRef(null);
  const frameRef = useRef(null);
  const [scale, setScale] = useState(0.7);
  const [height, setHeight] = useState(900);
  const doc = useMemo(() => buildPreviewDoc(post), [post]);

  const recalc = useCallback(() => {
    const w = wrapRef.current?.clientWidth || PREVIEW_VIEWPORT;
    setScale(w / PREVIEW_VIEWPORT);
    const d = frameRef.current?.contentDocument;
    if (d?.body) setHeight(d.body.scrollHeight);
  }, []);

  useEffect(() => {
    recalc();
    const id = setTimeout(recalc, 300);
    window.addEventListener('resize', recalc);
    return () => { clearTimeout(id); window.removeEventListener('resize', recalc); };
  }, [recalc, doc]);

  return (
    <div ref={wrapRef} style={{ width: '100%', height: Math.round(height * scale), overflow: 'hidden' }}>
      <iframe
        ref={frameRef}
        srcDoc={doc}
        title="Blog preview"
        onLoad={() => { recalc(); setTimeout(recalc, 500); }}
        style={{
          width: PREVIEW_VIEWPORT, height, border: 0, display: 'block',
          transform: `scale(${scale})`, transformOrigin: 'top left',
        }}
      />
    </div>
  );
}

/* Post preview — identical markup + styles to the public BlogPost page. */
function PostPreview({ post }) {
  return (
    <div className="tc-blogpost">
      {/* postStyles are injected raw into the iframe <head> by buildPreviewDoc;
          rendering them here would HTML-escape the CSS (breaking font-family). */}
      <header className="tc-blogpost-header">
        <div className="tc-blogpost-hero-grid">
          <div className="tc-blogpost-hero-text">
            {post.category && <p className="tc-blogpost-eyebrow">{post.category}</p>}
            <h1 className="tc-blogpost-title">{post.title || 'Untitled post'}</h1>
            <p className="tc-blogpost-meta">
              {formatDate(post.publishedAt)}
              {post.author && <> &middot; {post.author}</>}
              {post.readTime && <> &middot; {post.readTime}</>}
            </p>
          </div>
          <div className="tc-blogpost-hero-media">
            {post.coverImageUrl
              ? <img className="tc-blogpost-cover-img" src={post.coverImageUrl} alt={post.title} />
              : <div className="tc-blogpost-cover-img" style={{ background: post.coverGradient || '#e2e8f0' }} />}
          </div>
        </div>
      </header>
      <div className="tc-blogpost-divider"><hr /></div>
      <div className="tc-blogpost-main">
        <article
          className="tc-blogpost-body"
          dangerouslySetInnerHTML={{ __html: post.bodyHtml || '<p style="color:#94a3b8">Start writing…</p>' }}
        />
        <BlogPostSidebar preview />
      </div>
    </div>
  );
}
